import * as React from 'react';
import { cn } from '../../lib/utils';

export interface ContentContainerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Max content width. Defaults to 1440px (the platform-wide ceiling). */
  maxWidth?: 'narrow' | 'default' | 'wide' | 'full';
  /** Disable the default vertical padding (when stacking inside other containers). */
  flush?: boolean;
}

const maxWidthMap: Record<NonNullable<ContentContainerProps['maxWidth']>, string> = {
  narrow: 'max-w-[920px]',
  default: 'max-w-[1440px]',
  wide: 'max-w-[1680px]',
  full: 'max-w-none',
};

/**
 * ContentContainer — the canonical centered content wrapper used inside the
 * shell main column. Provides the responsive `px-5 md:px-7 lg:px-9` and
 * `py-6 md:py-7 lg:py-8` rhythm and clamps to a `1440px` content ceiling.
 */
export const ContentContainer = React.forwardRef<
  HTMLDivElement,
  ContentContainerProps
>(({ maxWidth = 'default', flush, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'mx-auto w-full px-5 md:px-7 lg:px-9',
        !flush && 'py-6 md:py-7 lg:py-8',
        maxWidthMap[maxWidth],
        className,
      )}
      {...props}
    />
  );
});
ContentContainer.displayName = 'ContentContainer';
